/**
 * Système d'analytics avancées pour tracking comportement utilisateur
 * Collecte et analyse les interactions pour optimiser l'expérience
 */

export const analytics = {
  // Configuration
  config: {
    debug: process.env.NODE_ENV === 'development',
    gtag: window.gtag || null,
    // Adresse de collecte (REACT_APP_ANALYTICS_ENDPOINT) : sans elle, aucun événement n'est envoyé
    endpoint: process.env.REACT_APP_ANALYTICS_ENDPOINT || null,
    userId: null,
    sessionId: null,
  },

  /**
   * Initialise le système d'analytics
   */
  init(userId = null) {
    this.config.userId = userId;
    this.config.sessionId = this.generateSessionId();
    
    if (this.config.debug) {
      console.log('📊 Analytics initialized', {
        userId: this.config.userId,
        sessionId: this.config.sessionId,
      });
    }

    // Écouter les changements de page
    this.setupPageTracking();
    
    // Écouter les interactions de base
    this.setupInteractionTracking();
    
    // Trackeer la session
    this.trackSessionStart();
  },

  /**
   * Track une page vue
   */
  trackPageView(pageName, additionalData = {}) {
    const eventData = {
      event: 'page_view',
      page_name: pageName,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
      ...additionalData,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag) {
      this.config.gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  },

  /**
   * Track un clic/interaction
   */
  trackClick(elementName, additionalData = {}) {
    const eventData = {
      event: 'click',
      element_name: elementName,
      page_url: window.location.href,
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
      ...additionalData,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag) {
      this.config.gtag('event', 'click', {
        event_category: 'engagement',
        event_label: elementName,
        value: 1,
      });
    }
  },

  /**
   * Track un investissement
   */
  trackInvestment(projectId, amount, method) {
    const eventData = {
      event: 'investment',
      project_id: projectId,
      amount: amount,
      payment_method: method,
      currency: 'XOF',
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
    };

    this.sendEvent(eventData);

    // Google Analytics Enhanced Ecommerce
    if (this.config.gtag) {
      this.config.gtag('event', 'purchase', {
        transaction_id: `inv_${Date.now()}`,
        value: amount,
        currency: 'XOF',
        items: [{
          item_id: projectId,
          item_name: `Project ${projectId}`,
          category: 'investment',
          quantity: 1,
          price: amount,
        }],
      });
    }
  },

  /**
   * Track une conversion (inscription, login, etc.)
   */
  trackConversion(conversionType, value = 1) {
    const eventData = {
      event: 'conversion',
      conversion_type: conversionType,
      value: value,
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag) {
      this.config.gtag('event', 'conversion', {
        event_category: 'goal',
        event_label: conversionType,
        value: value,
      });
    }
  },

  /**
   * Track le scroll sur une page
   */
  trackScroll(depth) {
    // Throttle le tracking du scroll
    if (this.lastScrollTrack && Date.now() - this.lastScrollTrack < 1000) {
      return;
    }
    
    this.lastScrollTrack = Date.now();

    const eventData = {
      event: 'scroll',
      scroll_depth: depth,
      page_url: window.location.href,
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag && depth >= 50) {
      this.config.gtag('event', 'scroll', {
        event_category: 'engagement',
        event_label: `${depth}%`,
        value: depth,
      });
    }
  },

  /**
   * Track le temps passé sur une page
   */
  trackTimeOnPage(pageName, timeSpent) {
    const eventData = {
      event: 'time_on_page',
      page_name: pageName,
      time_spent: timeSpent,
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag) {
      this.config.gtag('event', 'page_view_time', {
        event_category: 'engagement',
        event_label: pageName,
        value: Math.round(timeSpent / 1000), // en secondes
      });
    }
  },

  /**
   * Track une erreur
   */
  trackError(errorType, errorMessage, errorStack = null) {
    const eventData = {
      event: 'error',
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack,
      page_url: window.location.href,
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag) {
      this.config.gtag('event', 'exception', {
        description: `${errorType}: ${errorMessage}`,
        fatal: false,
      });
    }
  },

  /**
   * Track les interactions avec les projets
   */
  trackProjectInteraction(projectId, action, additionalData = {}) {
    const eventData = {
      event: 'project_interaction',
      project_id: projectId,
      action: action, // view, favorite, share, invest
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
      ...additionalData,
    };

    this.sendEvent(eventData);

    // Google Analytics
    if (this.config.gtag) {
      this.config.gtag('event', action, {
        event_category: 'project',
        event_label: projectId,
        value: 1,
      });
    }
  },

  /**
   * Setup du tracking automatique des pages
   */
  setupPageTracking() {
    let currentPage = window.location.pathname;
    let pageStartTime = Date.now();

    // Observer les changements de route
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== currentPage) {
        // Track le temps sur la page précédente
        const timeSpent = Date.now() - pageStartTime;
        this.trackTimeOnPage(currentPage, timeSpent);

        // Track la nouvelle page
        currentPage = window.location.pathname;
        pageStartTime = Date.now();
        this.trackPageView(currentPage);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Track la première page
    this.trackPageView(currentPage);
  },

  /**
   * Setup du tracking des interactions
   */
  setupInteractionTracking() {
    // Track les clics sur les liens
    document.addEventListener('click', (event) => {
      const target = event.target.closest('a, button');
      if (target) {
        const elementName = target.textContent?.trim() || 
                           target.getAttribute('aria-label') || 
                           target.className || 
                           'unknown_element';
        
        this.trackClick(elementName, {
          element_type: target.tagName.toLowerCase(),
          element_href: target.href || null,
        });
      }
    });

    // Track le scroll
    let ticking = false;
    document.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollDepth = Math.round(
            (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
          );
          
          if (scrollDepth > 0 && scrollDepth <= 100) {
            this.trackScroll(scrollDepth);
          }
          
          ticking = false;
        });
        ticking = true;
      }
    });
  },

  /**
   * Track le début de session
   */
  trackSessionStart() {
    const eventData = {
      event: 'session_start',
      timestamp: Date.now(),
      user_id: this.config.userId,
      session_id: this.config.sessionId,
      user_agent: navigator.userAgent,
      language: navigator.language,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      referrer: document.referrer,
    };

    this.sendEvent(eventData);
  },

  /**
   * Génère un ID de session unique
   */
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  },

  /**
   * Envoie un événement vers les services d'analytics
   */
  async sendEvent(eventData) {
    if (this.config.debug) {
      console.log('📊 Analytics Event:', eventData);
    }

    // Stocker localement pour debug
    this.storeEventLocally(eventData);

    // Envoyer vers endpoint personnalisé (uniquement en production)
    if (process.env.NODE_ENV === 'production' && this.config.endpoint) {
      try {
        await fetch(this.config.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(eventData),
        });
      } catch (error) {
        if (this.config.debug) {
          console.error('Failed to send analytics event:', error);
        }
      }
    }
  },

  /**
   * Stocke les événements localement pour debug
   */
  storeEventLocally(eventData) {
    if (!this.config.debug) return;

    const key = 'analytics_events';
    const stored = JSON.parse(localStorage.getItem(key) || '[]');
    stored.push(eventData);
    
    // Garder seulement les 200 derniers événements
    if (stored.length > 200) {
      stored.splice(0, stored.length - 200);
    }
    
    localStorage.setItem(key, JSON.stringify(stored));
  },

  /**
   * Récupère les événements stockés localement
   */
  getStoredEvents() {
    return JSON.parse(localStorage.getItem('analytics_events') || '[]');
  },

  /**
   * Génère un rapport d'analytics
   */
  generateReport() {
    const events = this.getStoredEvents();
    const report = {
      timestamp: Date.now(),
      session_id: this.config.sessionId,
      total_events: events.length,
      events_by_type: {},
      user_journey: [],
      engagement_metrics: {
        page_views: 0,
        clicks: 0,
        time_on_site: 0,
        scroll_depth_avg: 0,
      },
    };

    events.forEach((event) => {
      // Compter par type
      if (!report.events_by_type[event.event]) {
        report.events_by_type[event.event] = 0;
      }
      report.events_by_type[event.event]++;

      // Construire le parcours utilisateur
      if (['page_view', 'click', 'conversion'].includes(event.event)) {
        report.user_journey.push({
          event: event.event,
          timestamp: event.timestamp,
          details: event.page_name || event.element_name || event.conversion_type,
        });
      }

      // Calculer les métriques d'engagement
      if (event.event === 'page_view') {
        report.engagement_metrics.page_views++;
      }
      if (event.event === 'click') {
        report.engagement_metrics.clicks++;
      }
      if (event.event === 'time_on_page') {
        report.engagement_metrics.time_on_site += event.time_spent;
      }
    });

    return report;
  },
};

export default analytics;